/**
 * Combined auth page (one page for both flows). A single form serves both
 * actions: "Log in" accepts a username or email, "Create account" requires a
 * username (an email in the first field disables it — the optional email
 * field is for recovery). Each button enables only when its own requirements
 * are met; the profile replaces the form once signed in.
 */
import { useState } from 'react'
import { login, register, useAuthUser } from './auth.ts'
import { AuthError, AuthNotConfigured } from './ui.tsx'
import { ProfileCard } from './Profile.tsx'

export function AuthPage() {
  const { loading, configured, user, refresh } = useAuthUser()
  const [usernameOrEmail, setUsernameOrEmail] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [confirmedNoEmail, setConfirmedNoEmail] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!configured) return <AuthNotConfigured />
  if (loading) return <p className="muted">Loading…</p>
  if (user) return <ProfileCard user={user} />

  const isEmailInput = usernameOrEmail.includes('@')
  const loginReady = usernameOrEmail !== '' && password !== ''
  const createReady = loginReady && !isEmailInput && password.length >= 8
  const noEmail = email.trim() === ''

  const submitLogin = async () => {
    if (!loginReady) return
    setBusy(true)
    setError(null)
    const result = await login(usernameOrEmail.trim(), password)
    if (!result.ok) setError(result.error ?? 'Login failed.')
    setBusy(false)
  }

  const submitCreate = async () => {
    // Extra confirm tap for the deliberate choice to skip the email (PLAN.md §5).
    if (noEmail && !confirmedNoEmail) {
      setConfirmedNoEmail(true)
      return
    }
    setBusy(true)
    setError(null)
    const result = await register(usernameOrEmail.trim().toLowerCase(), password, email.trim())
    if (!result.ok) {
      setError(result.error ?? 'Registration failed.')
      setConfirmedNoEmail(false)
    } else {
      // the profile row landed after the sign-in event fired — re-read it
      refresh()
    }
    if (result.needsConfirmation) {
      setNotice(
        'Account created, but this deployment requires email confirmation — which the ' +
          'synthetic address cannot receive. Disable "Confirm email" in the Supabase dashboard.',
      )
    }
    setBusy(false)
  }

  const onPasswordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    if (loginReady) void submitLogin()
    else if (createReady) void submitCreate()
  }

  const createLabel =
    noEmail && confirmedNoEmail ? 'Confirm — create without email' : 'Create account'

  return (
    <div className="auth-page">
      <section className="question-card">
        <h2 className="question-card-title">Log in or create an account</h2>
        <AuthError message={error} />
        {notice ? <p className="drill-verdict">{notice}</p> : null}
        <label className="auth-field">
          <span>Username or email</span>
          <input
            type="text"
            autoComplete="username"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onPasswordKeyDown}
          />
        </label>
        <label className="auth-field">
          <span>
            Email <span className="muted">— optional, for account recovery</span>
          </span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {noEmail && (
          <p className="muted">
            Without an email we can't send you a reset link — if you forget your password, the
            account and everything in it is gone for good. You can add an email later from your
            profile.
          </p>
        )}
        <div className="auth-actions">
          <button
            type="button"
            className="question-submit"
            disabled={!loginReady || busy}
            onClick={() => void submitLogin()}
          >
            Log in
          </button>
          <button
            type="button"
            className="auth-secondary"
            disabled={!createReady || busy}
            onClick={() => void submitCreate()}
          >
            {busy ? 'Working…' : createLabel}
          </button>
        </div>
        <p className="muted">
          Logging in merges your guest history into the account automatically. Practising without
          an account works fine — everything stays on this device.
        </p>
      </section>
    </div>
  )
}
